import { AnimatePresence, motion, useAnimation } from "framer-motion";
import React, { useState } from "react";
import { SocialCardType } from "../types/SocialProof";
import useMediaQueries from "@hooks/useMediaQueries";
import {
  SocialCardLogoAnimation,
  SocialCardLogoMobAnimation,
  TextAnimation,
} from "../constants/Animations/SocialCard";
import { CtaButton } from "../Cta/cta-button";
import SPYellow from "../SVGs/SPYellow";
import useWindowSize from "@hooks/useWindowSize";

const SocialCard: React.FC<SocialCardType> = ({
  title,
  backgroundImage,
  customClass,
  quote,
  ctaCopy,
  ctaLink,
  customMouseEnter,
  customMouseLeave,
  minWidth,
  ctaUrl,
  onClick,
}) => {
  const spLogoAnimation = useAnimation();
  const { isTouch } = useMediaQueries();

  const [isHovered, setIsHovered] = useState(false);
  const [showTitle, setShowTitle] = useState(true);
  const { width: windowWidth } = useWindowSize();

  const socialCardLogoAnimationObj =
    windowWidth > 1023 ? SocialCardLogoAnimation : SocialCardLogoMobAnimation;

  const handleOpen = () => {
    if (customMouseEnter) customMouseEnter();
    setIsHovered(true);
    spLogoAnimation.start("visibleOne");

    setTimeout(() => {
      spLogoAnimation.start("visibleTwo");
    }, 350);
    setShowTitle(false);
  };

  const handleClose = () => {
    if (customMouseLeave) customMouseLeave();
    spLogoAnimation.start("hiddenAfterVisible");
    setTimeout(() => {
      setIsHovered(false);
      setTimeout(async () => {
        setShowTitle(true);
      }, 350);
    }, 170);
  };

  return (
    <div
      style={{
        ...(minWidth && {
          minWidth,
        }),
      }}
      className={`social--prof__card ${customClass}`}
      onMouseEnter={() => {
        if (!isTouch) handleOpen();
      }}
      onMouseLeave={handleClose}
      onClick={() => {
        if (!isTouch) return;

        if (isHovered) {
          handleClose();
        } else {
          handleOpen();
        }
      }}
    >
      <motion.div
        className="social--prof__card--background"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
        initial={{ opacity: 1 }}
        animate={{ opacity: isHovered ? 0 : 1 }}
        transition={{ duration: 0.5 }}
      />
      {showTitle && (
        <div
          className="social--prof__card--title social--prof__card__front--title"
          dangerouslySetInnerHTML={{ __html: title }}
        />
      )}
      <motion.div
        className="social--prof__card--icon"
        animate={spLogoAnimation}
        initial="hidden"
        variants={socialCardLogoAnimationObj}
      >
        <SPYellow />
      </motion.div>
      <AnimatePresence>
        {isHovered && (
          <>
            <motion.div
              className="social--prof__card__content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="social--prof__card--copy header5">
                <motion.h5
                  {...TextAnimation}
                  className="social--prof__card--copy__quote"
                >
                  {`"${quote}"`}
                </motion.h5>
                <motion.div
                  {...TextAnimation}
                  className="social--prof__card--title"
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              </div>
              {ctaCopy && (
                <motion.div className="flex" {...TextAnimation}>
                  <CtaButton
                    copy={ctaCopy}
                    copySize="bodyRegular"
                    isDisabled={false}
                    link={ctaLink || null}
                    linkUrl={ctaUrl || null}
                    variant="secondaryForest"
                    onClick={onClick}
                  />
                </motion.div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <motion.svg
        initial={{ scaleX: 0 }}
        animate={{ scaleX: isHovered ? 0.6 : 0, scaleY: isHovered ? 0.7 : 0 }}
        transition={{ duration: 0.5 }}
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="80"
        viewBox="0 0 32 80"
        fill="none"
        className="motion-svg-shape"
      >
        <path
          d="M31.9978 15.9107C11.4163 26.4865 -0.00436556 16.0112 -0.00436476 0.35408L-0.00557205 79.5831C-0.00449927 44.4359 9.34958 35.0357 31.9978 15.9107Z"
          fill="white"
        />
      </motion.svg>
    </div>
  );
};

export default SocialCard;
